import { UnitBasicInfoModel } from 'src/api/models';

// Mirrors the platform's HomeAvailabilityStatus. isAvailableForRent collapses four distinct states into one false (LOVE-335, LOVE-419).
export type HomeAvailabilityStatus =
  | 'NotListed'
  | 'ComingSoon'
  | 'Available'
  | 'OnHold'
  | 'PricingPending'
  | 'Rented';

// Temporary: availabilityStatus reaches models.ts on the next `pnpm generate-api`. Delete this block and the fallbacks below then.
declare module 'src/api/models' {
  interface UnitListingInfo {
    availabilityStatus?: HomeAvailabilityStatus;
  }
  interface UnitListingModel {
    availabilityStatus?: HomeAvailabilityStatus;
  }
  interface HomeMapPinModel {
    availabilityStatus?: HomeAvailabilityStatus;
  }
}

export type AvailabilitySignals = {
  availabilityStatus?: HomeAvailabilityStatus;
  isAvailableForRent?: boolean;
  isComingSoon?: boolean;
};

// No fallback: telling an on-hold home from a rented one is exactly what the old fields could not do.
export function isBlocked({
  availabilityStatus,
}: AvailabilitySignals): boolean {
  return (
    availabilityStatus === 'OnHold' || availabilityStatus === 'PricingPending'
  );
}

export function isAvailable({
  availabilityStatus,
  isAvailableForRent,
}: AvailabilitySignals): boolean {
  if (availabilityStatus) return availabilityStatus === 'Available';

  return !!isAvailableForRent;
}

export function isRented({
  availabilityStatus,
  isAvailableForRent,
  isComingSoon,
}: AvailabilitySignals): boolean {
  if (availabilityStatus) return availabilityStatus === 'Rented';

  return !isAvailableForRent && !isComingSoon;
}

const TOURABLE_MILESTONES: UnitBasicInfoModel['currentMilestone'][] = [
  'FullListingPublished',
  'ListingRepublished',
  'ShowReady',
];

type TourSignals = AvailabilitySignals & {
  currentMilestone?: UnitBasicInfoModel['currentMilestone'];
  isDemoUnit?: boolean;
};

// The allowlist gates the bottom of the touring window, the availability status the top.
export function isTourable({
  currentMilestone,
  isDemoUnit,
  ...signals
}: TourSignals): boolean {
  if (isDemoUnit || !currentMilestone) return false;
  if (!TOURABLE_MILESTONES.includes(currentMilestone)) return false;

  return !isBlocked(signals) && !isRented(signals);
}

export function showsComingSoonTreatment(
  signals: AvailabilitySignals,
): boolean {
  if (signals.availabilityStatus) {
    return signals.availabilityStatus === 'ComingSoon' || isBlocked(signals);
  }

  return !!signals.isComingSoon;
}
